import { useMemo } from "react";

/**
 * Permission format: "resource:action"
 *
 * @param {Object} params
 * @param {string[]} params.userPermissions - permissions from API
 * @param {string} [params.resource] - e.g. "organization"
 * @param {string} [params.action] - e.g. "create"
 * @param {string|string[]} [params.required] - override: explicit permission(s)
 * @param {"all"|"any"} [params.mode="all"] - how to evaluate multiple required permissions
 * @param {boolean} [params.enabled=true] - allow temporarily bypass (e.g. while loading)
 */
export function useCan({
  userPermissions,
  resource,
  action,
  required,
  mode = "all",
  enabled = true,
}) {
  const requiredList = useMemo(() => {
    if (required) return Array.isArray(required) ? required : [required];
    if (resource && action) return [`${resource}:${action}`];
    return [];
  }, [required, resource, action]);

  const allowed = useMemo(() => {
    if (!enabled) return false;
    if (!Array.isArray(userPermissions) || userPermissions.length === 0)
      return false;
    if (requiredList.length === 0) return false;

    const set = new Set(userPermissions);
    return mode === "any"
      ? requiredList.some((p) => set.has(p))
      : requiredList.every((p) => set.has(p));
  }, [enabled, userPermissions, requiredList, mode]);

  return { allowed, required: requiredList };
}
